import { FC, useCallback, useState } from 'react';
import { Modal } from '@/components/ui';
import { Step1ProductService } from '@/components/wizard/Step1ProductService';
import { Step2Company } from '@/components/wizard/Step2Company';
import { Step2Competitors } from '@/components/wizard/Step2Competitors';
import { Step3LinkedIn } from '@/components/wizard/Step3LinkedIn';
import { Step4ContentTypes } from '@/components/wizard/Step4ContentTypes';
import { Step5SalesTargets } from '@/components/wizard/Step5SalesTargets';
import { Step6PitchStrategy } from '@/components/wizard/Step6PitchStrategy';
import { Step7Calendly } from '@/components/wizard/Step7Calendly';
import { wizardService } from '@/services';
import type { WizardStatus } from '@/hooks/useWizard';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/error';

interface WizardStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepNumber: number;
  wizardData: WizardStatus['data'] | undefined;
  onSuccess: () => void;
}

const stepTitles: Record<number, string> = {
  1: 'Product/Service & Industry',
  2: 'Company Profile',
  3: 'Competitors',
  4: 'LinkedIn Company Page',
  5: 'Content Generation',
  6: 'Sales Targets',
  7: 'Pitch Strategy',
  8: 'Connect Calendar',
};

export const WizardStepModal: FC<WizardStepModalProps> = ({
  isOpen,
  onClose,
  stepNumber,
  wizardData,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = useCallback(
    async (stepData: any) => {
      try {
        setIsSubmitting(true);

        // Map step data to the correct format for each step
        switch (stepNumber) {
          case 1:
            await wizardService.saveStep1(stepData);
            break;
          case 2:
            await wizardService.saveStep2(stepData);
            break;
          case 3:
            await wizardService.saveStep3(stepData);
            break;
          case 4:
            await wizardService.saveStep4(stepData);
            break;
          case 5:
            await wizardService.saveStep5(stepData);
            break;
          case 6:
            await wizardService.saveStep6(stepData);
            break;
          case 7:
            await wizardService.saveStep7(stepData);
            break;
          case 8:
            await wizardService.saveStep8(stepData);
            break;
          default:
            throw new Error(`Invalid step number: ${stepNumber}`);
        }

        // Show success toast
        addToast('Changes saved successfully!', 'success');

        // Refetch data to get updated values
        await onSuccess();

        // Close modal after a short delay to show success state
        setTimeout(() => {
          onClose();
        }, 800);
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err);
        addToast(errorMsg, 'error');
        console.error(`Error saving step ${stepNumber}:`, err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [stepNumber, onClose, onSuccess, addToast]
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Get initial data for the specific step
  const getInitialData = (): any => {
    if (!wizardData) return undefined;

    switch (stepNumber) {
      case 1:
        return {
          productOrService: wizardData.productOrService,
          industry: wizardData.industry,
          description: wizardData.description,
          serviceDescription: wizardData.serviceDescription,
        };
      case 2:
        return {
          keyPeople: wizardData.keyPeople,
          brandColors: wizardData.brandColors,
          logoUrl: wizardData.logoUrl,
          brandGuidelines: wizardData.brandGuidelines,
          country: wizardData.country,
          countryCode: wizardData.countryCode,
          timezone: wizardData.timezone,
        };
      case 3:
        return {
          competitors: wizardData.competitors,
        };
      case 4:
        return {
          linkedinUrl: wizardData.linkedinUrl,
        };
      case 5:
        return {
          contentTypes: wizardData.contentTypes,
          dailyPostCount: wizardData.dailyPostCount,
        };
      case 6:
        return {
          salesTarget: wizardData.salesTarget,
        };
      case 7:
        return {
          pitchStrategy: wizardData.pitchStrategy,
        };
      case 8:
        return {
          calendlyLink: wizardData.calendlyLink,
        };
      default:
        return undefined;
    }
  };


  const renderStepComponent = () => {
    const initialData = getInitialData();
    const commonProps = {
      initialData,
      onNext: handleSubmit,
      onBack: undefined, // No back button in modal edit mode
      isSubmitting, // Pass loading state
      skipReset: isSubmitting, // Prevent reset during submission
    };

    switch (stepNumber) {
      case 1:
        return <Step1ProductService {...commonProps} />;
      case 2:
        return <Step2Company {...commonProps} />;
      case 3:
        return <Step2Competitors {...commonProps} />;
      case 4:
        return <Step3LinkedIn {...commonProps} />;
      case 5:
        return <Step4ContentTypes {...commonProps} />;
      case 6:
        return <Step5SalesTargets {...commonProps} />;
      case 7:
        return <Step6PitchStrategy {...commonProps} />;
      case 8:
        return <Step7Calendly {...commonProps} />;
      default:
        return <div>Invalid step number</div>;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={stepTitles[stepNumber]}
      size={stepNumber === 2 ? "lg" : "md"}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <div className={`${isSubmitting ? 'pointer-events-none opacity-75' : ''} py-2`}>
        {renderStepComponent()}
      </div>
    </Modal>
  );
};

